import { findSceneNode, getAllSceneNodes, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import { frameAutoLayoutMode, nodeConstraints, type SceneNode } from "@/document/model/nodes";
import { applyConstraintsToBounds } from "@/document/model/constraints";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import { getVectorNetworkBounds } from "@/document/operations/vectorGeometry";
import { translateConnectedHandles } from "@/document/operations/vectorVertexMerge";
import { frameUsesPatternLayout } from "@/document/operations/patternLayout";
import { applyBoundsToNode } from "@/document/operations/resizeNode";
import {
  nodeAncestorChain,
  nodeDisplayWorldTransform,
  nodeSelectionPointToWorld,
  type ScenePreview,
} from "@/document/operations/nodeWorldTransform";
import {
  affineEquals,
  affineRotation,
  applyAffinePoint,
  boundsFromAffineRect,
  IDENTITY_AFFINE,
  multiplyAffine,
  type Affine2D,
} from "@/lib/math/affine";
import { boundsFromPoints, rectCenter, rectCorners, rotatePoint } from "@/lib/math/rotation";
import type { RenderOverlay, RenderPreview } from "../overlayTypes";
import type { DisplayClip, DisplayItem, DisplayPaint } from "./displayList";
import { sceneNodesToDisplayItems, sceneNodeToDisplayItemsWithParent } from "./tessellation";
import {
  expandHiddenSubtreeIds,
  filterDisplayListByHiddenIds,
} from "./displayListVisibility";
import { buildPaintOrderItems, splitItemsByRenderClass } from "../sceneComposition";
import { offsetFlatVertices, rectVertices } from "../renderHelpers";
import { patchRenderTelemetry } from "../renderTelemetry";
import { packMesh } from "./tessellation/meshKernels";
import { getGeometryPool } from "./tessellation/geometryPool";
import { getSharedRetainedScene, type RetainedScene } from "./retainedScene";

export interface VectorNetworkPreview {
  id: string;
  network: VectorNetwork;
  bounds: RenderPreview["bounds"];
  changedVertexIds: string[];
  changedEdgeIds: string[];
}

interface PreviewItemTransform {
  bounds: RenderPreview["bounds"];
  rotation?: number;
  rotationDelta?: number;
  transformClip: boolean;
  clip?: DisplayClip;
  worldOriginDelta?: { x: number; y: number };
  worldTransform?: Affine2D;
}

export interface PreparedSceneFrame {
  document: CurvaDocument;
  revision: number;
  tileVariantKey: string;
  sceneSignature: string;
  tileableItems: DisplayItem[];
  liveRasterItems: DisplayItem[];
  liveBackdropItems: DisplayItem[];
  dynamicItems: DisplayItem[];
  copyPreviewItems: DisplayItem[];
  draftVectorItems: DisplayItem[];
  draftComponentItems: DisplayItem[];
  paintOrderItems: DisplayItem[];
  overlayLookupItems: DisplayItem[];
  vectorPreview: VectorNetworkPreview | undefined;
  textPreviewNodeIds: Set<string>;
}

export interface PrepareSceneFrameOptions {
  renderTrianglesDebug?: boolean;
  retainedScene?: RetainedScene;
}

export function prepareSceneFrame(
  document: CurvaDocument,
  overlay: RenderOverlay,
  sceneSignature: string,
  options: PrepareSceneFrameOptions = {},
): PreparedSceneFrame {
  const renderTrianglesDebug = options.renderTrianglesDebug ?? false;
  const retainedScene = options.retainedScene ?? getSharedRetainedScene();
    const movePreviews = overlay.previews.filter(preview => preview.nodeId !== "__marquee__" && preview.mode !== "copy");
    const copyPreviews = overlay.previews.filter(preview => preview.nodeId !== "__marquee__" && preview.mode === "copy");
    const previewById = new Map(movePreviews.map(preview => [preview.nodeId, preview] as const));
    const copyPreviewById = new Map(copyPreviews.map(preview => [preview.nodeId, preview] as const));
    const vectorPreview = previewVectorNetwork(document, overlay);
    const vectorNetworkPreviews = vectorPreview ? new Map([[vectorPreview.id, { network: vectorPreview.network, bounds: vectorPreview.bounds }]]) : undefined;
    const booleanSourcePreviews = booleanSourcePreviewBounds(document, previewById);
    const hiddenNodeIds = overlay.hiddenNodeIds?.length ? new Set(overlay.hiddenNodeIds) : undefined;
    const hiddenNodeIdsPreserveOverlayLookup = overlay.hiddenNodeIdsPreserveOverlayLookup?.length
      ? new Set(overlay.hiddenNodeIdsPreserveOverlayLookup)
      : undefined;
    const paintHiddenNodeIds = hiddenNodeIds || hiddenNodeIdsPreserveOverlayLookup
      ? new Set([...(hiddenNodeIds ?? []), ...(hiddenNodeIdsPreserveOverlayLookup ?? [])])
      : undefined;
    const needsRichTessellation = Boolean(
      vectorNetworkPreviews
      || booleanSourcePreviews
      || overlay.textPreviews?.size
      || paintHiddenNodeIds?.size,
    );
    const needsOverlayLookupSplit = Boolean(hiddenNodeIdsPreserveOverlayLookup?.size);
    const tessellationOptions = {
      activePageId: overlay.activePageId,
      vectorNetworkPreviews,
      booleanSourcePreviews,
      textPreviews: overlay.textPreviews,
      hiddenNodeIds: needsOverlayLookupSplit ? hiddenNodeIds : paintHiddenNodeIds,
    };
    const tessellationStart = performance.now();
    getGeometryPool().resetStats();
    const tessellatedDisplayList = needsRichTessellation
      ? retainedScene.ensureDisplayList(document, tessellationOptions)
      : retainedScene.ensureDisplayList(document, { activePageId: overlay.activePageId });
    const displayList = needsOverlayLookupSplit && paintHiddenNodeIds
      ? filterDisplayListByHiddenIds(
        tessellatedDisplayList,
        expandHiddenSubtreeIds(document, paintHiddenNodeIds, overlay.activePageId),
      )
      : tessellatedDisplayList;
    const poolStats = getGeometryPool().stats();
    patchRenderTelemetry({
      tessellationMs: performance.now() - tessellationStart,
      geometryPoolHits: poolStats.hits,
    });
    const overlayLookupDisplayList = tessellatedDisplayList;
    const previewStart = performance.now();
    const previewItemTransforms = previewById.size > 0
      ? buildPreviewItemTransforms(document, previewById, overlay.ignoreChildConstraints)
      : new Map<string, PreviewItemTransform>();
    const copyPreviewItemTransforms = copyPreviewById.size > 0
      ? buildPreviewItemTransforms(document, copyPreviewById, overlay.ignoreChildConstraints)
      : new Map<string, PreviewItemTransform>();
    const moveDynamicItemIds = previewById.size > 0 || vectorPreview
      ? collectDynamicItemIds(document, previewById, vectorPreview?.id)
      : new Set<string>();
    const textPreviewItemIds = overlay.textPreviews?.size
      ? new Set(overlay.textPreviews.keys())
      : new Set<string>();
    const dynamicItemIds = new Set([...moveDynamicItemIds, ...textPreviewItemIds]);
    const copyDynamicItemIds = copyPreviewById.size > 0
      ? collectDynamicItemIds(document, copyPreviewById, undefined)
      : new Set<string>();
    // Resize previews re-tessellate the affected node subtree through the same
    // geometry path as the commit (applyBoundsToNode + tessellation) so the
    // preview is pixel-identical to the committed result — most importantly,
    // strokes keep their true width instead of scaling with the bounds.
    const resizePreview = buildResizePreviewItems(document, previewById, displayList.items, overlay.ignoreChildConstraints);
    const emittedResizeRoots = new Set<string>();
    const renderedItems: DisplayItem[] = [];
    for (const item of displayList.items) {
      const rootId = resizePreview.rootByNodeId.get(item.id);
      if (rootId !== undefined) {
        if (!emittedResizeRoots.has(rootId)) {
          emittedResizeRoots.add(rootId);
          renderedItems.push(...(resizePreview.itemsByRoot.get(rootId) ?? []));
        }
        continue;
      }
      renderedItems.push(previewDisplayItem(item, previewItemTransforms.get(item.id)));
    }
    const copyPreviewItems = copyPreviewById.size > 0
      ? displayList.items
        .filter(item => copyDynamicItemIds.has(item.id))
        .map(item => previewDisplayItem(item, copyPreviewItemTransforms.get(item.id)))
      : [];
    const overlayLookupRenderedItems = hiddenNodeIdsPreserveOverlayLookup
      ? overlayLookupDisplayList.items.map(item => previewDisplayItem(item, previewItemTransforms.get(item.id)))
      : renderedItems;
    const draftVectorItems = overlay.draftVectorNetwork
      ? sceneNodesToDisplayItems(document, [overlay.draftVectorNetwork.node], overlay.draftVectorNetwork.clip ? rectClip(overlay.draftVectorNetwork.clip) : undefined)
      : [];
    const draftComponentItems = overlay.draftComponentInstance
      ? sceneNodesToDisplayItems(document, [overlay.draftComponentInstance.node])
      : [];
    patchRenderTelemetry({ previewMs: performance.now() - previewStart });
    const classifyStart = performance.now();
    const paintOrderItems = buildPaintOrderItems(renderedItems, copyPreviewItems);
    const overlayLookupItems = copyPreviewItems.length > 0 ? [...copyPreviewItems, ...overlayLookupRenderedItems] : overlayLookupRenderedItems;
    const classified = splitItemsByRenderClass(renderedItems, dynamicItemIds);
    patchRenderTelemetry({ classifyMs: performance.now() - classifyStart });

    return {
      document,
      revision: document.revision,
      tileVariantKey: `classified:${overlay.activePageId ?? "default"}:${renderTrianglesDebug ? "triangles" : "normal"}`,
      sceneSignature,
      tileableItems: classified.tileable,
      liveRasterItems: classified.liveRaster,
      liveBackdropItems: classified.liveBackdrop,
      dynamicItems: classified.dynamic,
      copyPreviewItems,
      draftVectorItems,
      draftComponentItems,
      paintOrderItems,
      overlayLookupItems,
      vectorPreview,
      textPreviewNodeIds: textPreviewItemIds,
    };
}

interface ResizePreviewItems {
  rootByNodeId: Map<string, string>;
  itemsByRoot: Map<string, DisplayItem[]>;
}

/**
 * Re-tessellate previewed subtrees when a bounds remap would be wrong:
 * - resizes (stroke width must not scale with the preview bounds remap)
 * - pattern (repeat/mirror) frames: multiple display items share one child id with
 *   instance transforms, so remapping every copy onto the same previewBounds
 *   collapses virtual copies onto the prototype
 */
function buildResizePreviewItems(
  document: CurvaDocument,
  previewById: Map<string, RenderPreview>,
  committedItems: DisplayItem[],
  ignoreChildConstraints = false,
): ResizePreviewItems {
  const rootByNodeId = new Map<string, string>();
  const itemsByRoot = new Map<string, DisplayItem[]>();
  if (previewById.size === 0) {
    return { rootByNodeId, itemsByRoot };
  }

  const candidates = new Map<string, { node: SceneNode; preview: RenderPreview }>();
  for (const [nodeId, preview] of previewById) {
    const node = findSceneNode(document, nodeId);
    if (!node || !canRetessellateResizePreview(node)) {
      continue;
    }
    const widthChanged = Math.abs(node.bounds.width - preview.bounds.width) > 0.001;
    const heightChanged = Math.abs(node.bounds.height - preview.bounds.height) > 0.001;
    const patternMove = node.type === "frame" && frameUsesPatternLayout(node);
    if (widthChanged || heightChanged || patternMove) {
      candidates.set(nodeId, { node, preview });
    }
  }
  if (candidates.size === 0) {
    return { rootByNodeId, itemsByRoot };
  }

  // A resized subtree is re-tessellated whole, so drop any candidate nested
  // inside another candidate to avoid tessellating it twice.
  const clipByNodeId = new Map<string, DisplayItem["clip"]>();
  for (const item of committedItems) {
    if (!clipByNodeId.has(item.id)) {
      clipByNodeId.set(item.id, item.clip);
    }
  }

  for (const [nodeId, { node, preview }] of candidates) {
    const nestedInsideOtherCandidate = nodeAncestorChain(document, nodeId).some(ancestor => candidates.has(ancestor.id));
    if (nestedInsideOtherCandidate) {
      continue;
    }
    if (!clipByNodeId.has(nodeId)) {
      // Node currently contributes no display items (e.g. empty) — nothing to replace.
      continue;
    }

    const clone = structuredClone(node);
    applyBoundsToNode(clone, preview.bounds, { ignoreChildConstraints });
    if (preview.rotation !== undefined) {
      clone.transform = { ...clone.transform, rotation: preview.rotation };
    }

    const ancestors = nodeAncestorChain(document, nodeId);
    const parentId = ancestors.at(-1)?.id;
    const parentWorldTransform = parentId ? nodeDisplayWorldTransform(document, parentId) : IDENTITY_AFFINE;
    const activeClip = clipByNodeId.get(nodeId);
    const items = sceneNodeToDisplayItemsWithParent(document, clone, activeClip, parentWorldTransform);

    for (const subtreeId of collectSubtreeNodeIds(node)) {
      rootByNodeId.set(subtreeId, nodeId);
    }
    itemsByRoot.set(nodeId, items);
  }

  return { rootByNodeId, itemsByRoot };
}

function canRetessellateResizePreview(node: SceneNode): boolean {
  if (node.type === "text") {
    // Text scales via its transform (font size), handled by the text preview path.
    return false;
  }
  if (node.type === "frame" && frameUsesPatternLayout(node)) {
    // Pattern frames expand virtual copies at tessellation time; remapping the
    // committed items by node id collapses those copies.
    return true;
  }
  if (node.type === "frame" && frameAutoLayoutMode(node) !== "off") {
    // Flex auto-layout requires the full finalize pass; leave to the transform path.
    return false;
  }
  return true;
}

function collectSubtreeNodeIds(node: SceneNode, ids: Set<string> = new Set()): Set<string> {
  ids.add(node.id);
  const children = getSceneNodeChildren(node);
  if (children) {
    for (const child of children) {
      collectSubtreeNodeIds(child, ids);
    }
  }
  return ids;
}

function rectClip(bounds: RenderPreview["bounds"]): DisplayClip {
  return {
    bounds,
    polygon: rectCorners(bounds),
  };
}

function transformedRectClip(bounds: RenderPreview["bounds"], worldTransform?: Affine2D): DisplayClip {
  const polygon = rectCorners(bounds).map(point => worldTransform ? applyAffinePoint(worldTransform, point) : point);
  return {
    bounds: boundsFromPoints(polygon),
    polygon,
  };
}

function intersectDisplayClips(left: DisplayClip | undefined, right: DisplayClip): DisplayClip {
  if (!left) {
    return right;
  }

  const polygon = intersectConvexClipPolygons(left.polygon, right.polygon);
  return {
    bounds: polygon.length >= 3 ? boundsFromPoints(polygon) : { x: 0, y: 0, width: 0, height: 0 },
    polygon,
  };
}

function intersectConvexClipPolygons(
  subject: Array<{ x: number; y: number }>,
  clip: Array<{ x: number; y: number }>,
): Array<{ x: number; y: number }> {
  if (subject.length < 3 || clip.length < 3) {
    return [];
  }

  let output = subject;
  const winding = polygonSignedArea(clip) >= 0 ? 1 : -1;
  for (let index = 0; index < clip.length; index += 1) {
    const a = clip[index]!;
    const b = clip[(index + 1) % clip.length]!;
    const input = output;
    output = [];
    if (input.length === 0) {
      break;
    }

    let previous = input[input.length - 1]!;
    let previousInside = pointInsideClipEdge(previous, a, b, winding);
    for (const current of input) {
      const currentInside = pointInsideClipEdge(current, a, b, winding);
      if (currentInside) {
        if (!previousInside) {
          output.push(clipEdgeIntersection(previous, current, a, b));
        }
        output.push(current);
      } else if (previousInside) {
        output.push(clipEdgeIntersection(previous, current, a, b));
      }
      previous = current;
      previousInside = currentInside;
    }
  }

  return output;
}

function pointInsideClipEdge(
  point: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
  winding: number,
): boolean {
  const cross = (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x);
  return winding > 0 ? cross >= -0.0001 : cross <= 0.0001;
}

function clipEdgeIntersection(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
): { x: number; y: number } {
  const dx1 = p2.x - p1.x;
  const dy1 = p2.y - p1.y;
  const dx2 = b.x - a.x;
  const dy2 = b.y - a.y;
  const denominator = dx1 * dy2 - dy1 * dx2;
  if (Math.abs(denominator) < 0.000001) {
    return p2;
  }

  const t = ((a.x - p1.x) * dy2 - (a.y - p1.y) * dx2) / denominator;
  return {
    x: p1.x + t * dx1,
    y: p1.y + t * dy1,
  };
}

function polygonSignedArea(points: Array<{ x: number; y: number }>): number {
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]!;
    const next = points[(index + 1) % points.length]!;
    area += current.x * next.y - next.x * current.y;
  }
  return area / 2;
}

function previewDisplayItem(item: DisplayItem, previewTransform: PreviewItemTransform | undefined): DisplayItem {
  if (!previewTransform) {
    return item;
  }

  const previewBounds = previewTransform.bounds;
  const clip = previewTransform.clip ?? (previewTransform.transformClip && item.clip ? transformDisplayClip(item.clip, item.bounds, previewBounds) : item.clip);
  const rotationDelta = previewTransform.rotationDelta ?? 0;
  const rotationCenter = rectCenter(previewBounds);
  const boundsDx = previewBounds.x - item.bounds.x;
  const boundsDy = previewBounds.y - item.bounds.y;
  const worldDx = previewTransform.worldOriginDelta?.x ?? boundsDx;
  const worldDy = previewTransform.worldOriginDelta?.y ?? boundsDy;
  const pureMove = rotationDelta === 0 && boundsArePureMove(item.bounds, previewBounds);
  const previewWorldTransform = resolvePreviewWorldTransform(item, previewTransform, rotationCenter);
  const previewVisualBounds = previewItemVisualBounds(item, previewBounds, previewWorldTransform, worldDx, worldDy);
  const usesWorldVertexTranslation = !item.worldTransform && item.kind !== "text" && item.fill.type !== "image";

  if (item.kind === "text") {
    return {
      ...item,
      bounds: previewBounds,
      visualBounds: previewVisualBounds,
      clip,
      worldTransform: previewWorldTransform,
    };
  }

  if (item.fill.type === "image") {
    const fill = previewDisplayPaint(item.fill, item.bounds, previewBounds);
    const vertices = item.vertices.length > 0
      ? transformVertices(item.vertices, item.bounds, previewBounds)
      : packMesh(rectVertices(previewBounds));
    return {
      ...item,
      bounds: previewBounds,
      visualBounds: previewVisualBounds,
      clip,
      fill,
      worldTransform: previewWorldTransform,
      vertices,
      positionOffset: undefined,
    };
  }

  if (previewWorldTransform && item.localVertices) {
    const localVertices = transformVertices(item.localVertices, item.bounds, previewBounds);
    const vertices = transformFlatVerticesByAffine(localVertices, previewWorldTransform);
    const localFill = previewDisplayPaint(item.localFill ?? item.fill, item.bounds, previewBounds);
    const fill = transformDisplayPaintByAffine(localFill, previewWorldTransform);
    const shadowContours = item.localShadowContours?.map(contour =>
      contour.map(point => {
        const scaled = transformDisplayPoint([point.x, point.y], item.bounds, previewBounds);
        return applyAffinePoint(previewWorldTransform, { x: scaled[0], y: scaled[1] });
      }),
    );
    return {
      ...item,
      bounds: previewBounds,
      visualBounds: boundsFromVertices(vertices),
      localVertices,
      vertices,
      fill,
      shadowContours,
      clip,
      positionOffset: undefined,
    };
  }

  if (item.worldTransform && (item.kind === "ellipse" || item.kind === "roundedRect")) {
    // Legacy path: bake any remaining rotated SDF items to triangles (same as tessellation).
    const localVertices = item.vertices.length > 0
      ? transformVertices(item.vertices, item.bounds, previewBounds)
      : packMesh(rectVertices(previewBounds));
    const matrix = previewWorldTransform ?? item.worldTransform;
    const vertices = transformFlatVerticesByAffine(localVertices, matrix);
    return {
      ...item,
      kind: "triangles",
      cornerRadii: undefined,
      bounds: previewBounds,
      visualBounds: boundsFromVertices(vertices),
      clip,
      localVertices,
      localFill: previewDisplayPaint(item.fill, item.bounds, previewBounds),
      fill: transformDisplayPaintByAffine(
        previewDisplayPaint(item.fill, item.bounds, previewBounds),
        matrix,
      ),
      worldTransform: undefined,
      vertices,
      positionOffset: undefined,
    };
  }

  const fill = pureMove && usesWorldVertexTranslation
    ? offsetDisplayPaint(item.fill, worldDx, worldDy)
    : previewDisplayPaint(item.fill, item.bounds, previewBounds);
  if (pureMove) {
    if (item.worldTransform) {
      return {
        ...item,
        bounds: previewBounds,
        visualBounds: previewVisualBounds,
        worldTransform: previewWorldTransform,
        clip,
        positionOffset: undefined,
      };
    }

    const vertexDx = usesWorldVertexTranslation ? worldDx : boundsDx;
    const vertexDy = usesWorldVertexTranslation ? worldDy : boundsDy;
    return {
      ...item,
      bounds: previewBounds,
      visualBounds: previewVisualBounds ?? (item.visualBounds ? offsetBounds(item.visualBounds, vertexDx, vertexDy) : undefined),
      vertices: offsetFlatVertices(item.vertices, vertexDx, vertexDy),
      shadowContours: item.shadowContours?.map(contour =>
        contour.map(point => ({ x: point.x + vertexDx, y: point.y + vertexDy })),
      ),
      fill,
      clip,
      positionOffset: undefined,
    };
  }

  if (item.kind === "ellipse" || item.kind === "roundedRect") {
    const vertices = rotationDelta === 0
      ? transformVertices(item.vertices, item.bounds, previewBounds)
      : rotatePreviewVertices(rectVertices(previewBounds), rotationCenter, rotationDelta);
    const visualBounds = boundsFromVertices(vertices);
    return {
      ...item,
      kind: rotationDelta === 0 ? item.kind : "triangles",
      cornerRadii: rotationDelta === 0 ? item.cornerRadii : undefined,
      bounds: previewBounds,
      visualBounds: boundsEqual(visualBounds, previewBounds) ? undefined : visualBounds,
      vertices,
      fill: rotationDelta === 0 ? fill : rotatePreviewPaint(fill, rotationCenter, rotationDelta),
      clip,
      positionOffset: undefined,
    };
  }

  const transformedVertices = transformVertices(item.vertices, item.bounds, previewBounds);
  const vertices = rotationDelta === 0
    ? transformedVertices
    : rotatePreviewVertices(transformedVertices, rotationCenter, rotationDelta);
  const visualBounds = boundsFromVertices(vertices);
  return {
    ...item,
    bounds: previewBounds,
    visualBounds: boundsEqual(visualBounds, previewBounds) ? undefined : visualBounds,
    positionOffset: undefined,
    vertices,
    fill: rotationDelta === 0 ? fill : rotatePreviewPaint(fill, rotationCenter, rotationDelta),
    clip,
  };
}

function resolvePreviewWorldTransform(
  item: DisplayItem,
  previewTransform: PreviewItemTransform,
  rotationCenter: { x: number; y: number },
): Affine2D | undefined {
  if (previewTransform.worldTransform && !affineEquals(previewTransform.worldTransform, IDENTITY_AFFINE)) {
    return previewTransform.worldTransform;
  }

  if (item.worldTransform) {
    return previewWorldTransformForItem(item.worldTransform, previewTransform.rotationDelta ?? 0, rotationCenter);
  }

  return previewTransform.worldTransform;
}

function previewItemVisualBounds(
  item: DisplayItem,
  previewBounds: RenderPreview["bounds"],
  previewWorldTransform: Affine2D | undefined,
  worldDx: number,
  worldDy: number,
): RenderPreview["bounds"] | undefined {
  if (previewWorldTransform && !affineEquals(previewWorldTransform, IDENTITY_AFFINE)) {
    return boundsFromAffineRect(previewWorldTransform, previewBounds);
  }

  if (!item.visualBounds) {
    return undefined;
  }

  return offsetBounds(item.visualBounds, worldDx, worldDy);
}

function previewWorldTransformForItem(
  worldTransform: Affine2D | undefined,
  rotationDelta: number,
  rotationCenter: { x: number; y: number },
): Affine2D | undefined {
  if (rotationDelta === 0) {
    return worldTransform;
  }

  return multiplyAffine(affineRotation(rotationDelta, rotationCenter), worldTransform ?? IDENTITY_AFFINE);
}

export function previewDisplayItemForTest(
  item: DisplayItem,
  preview: RenderPreview["bounds"],
  options: {
    rotationDelta?: number;
    clip?: DisplayClip;
    worldOriginDelta?: { x: number; y: number };
    worldTransform?: Affine2D;
  } = {},
): DisplayItem {
  return previewDisplayItem(item, {
    bounds: preview,
    rotationDelta: options.rotationDelta,
    clip: options.clip,
    transformClip: false,
    worldOriginDelta: options.worldOriginDelta,
    worldTransform: options.worldTransform,
  });
}

export function previewDisplayItemsForTest(document: CurvaDocument, items: DisplayItem[], previews: RenderPreview[]): DisplayItem[] {
  const previewById = new Map(previews.filter(preview => preview.nodeId !== "__marquee__").map(preview => [preview.nodeId, preview] as const));
  const transforms = buildPreviewItemTransforms(document, previewById);
  return items.map(item => previewDisplayItem(item, transforms.get(item.id)));
}

function previewDisplayPaint(paint: DisplayPaint, fromBounds: RenderPreview["bounds"], toBounds: RenderPreview["bounds"]): DisplayPaint {
  if (paint.type === "image") {
    return {
      ...paint,
      imageRect: transformBounds(paint.imageRect, fromBounds, toBounds),
    };
  }

  if (paint.type === "linearGradient") {
    return {
      ...paint,
      start: transformDisplayPoint(paint.start, fromBounds, toBounds),
      end: transformDisplayPoint(paint.end, fromBounds, toBounds),
    };
  }

  if (paint.type === "radialGradient") {
    const center = transformDisplayPoint(paint.center, fromBounds, toBounds);
    const radiusScale = Math.max(
      fromBounds.width === 0 ? 1 : toBounds.width / fromBounds.width,
      fromBounds.height === 0 ? 1 : toBounds.height / fromBounds.height,
    );
    return {
      ...paint,
      center,
      radius: paint.radius * radiusScale,
    };
  }

  return paint;
}

export function transformDisplayPoint(
  point: [number, number],
  fromBounds: RenderPreview["bounds"],
  toBounds: RenderPreview["bounds"],
): [number, number] {
  const scaleX = fromBounds.width === 0 ? 1 : toBounds.width / fromBounds.width;
  const scaleY = fromBounds.height === 0 ? 1 : toBounds.height / fromBounds.height;
  return [
    toBounds.x + (point[0] - fromBounds.x) * scaleX,
    toBounds.y + (point[1] - fromBounds.y) * scaleY,
  ];
}

function transformDisplayClip(
  clip: DisplayClip,
  fromBounds: RenderPreview["bounds"],
  toBounds: RenderPreview["bounds"],
): DisplayClip {
  const polygon = clip.polygon.map(point => {
    const transformed = transformDisplayPoint([point.x, point.y], fromBounds, toBounds);
    return { x: transformed[0], y: transformed[1] };
  });
  return {
    bounds: transformBounds(clip.bounds, fromBounds, toBounds),
    polygon,
  };
}

export function boundsEqual(left: RenderPreview["bounds"], right: RenderPreview["bounds"]): boolean {
  return nearlyEqual(left.x, right.x) &&
    nearlyEqual(left.y, right.y) &&
    nearlyEqual(left.width, right.width) &&
    nearlyEqual(left.height, right.height);
}

function nearlyEqual(left: number, right: number): boolean {
  return Math.abs(left - right) <= 0.001;
}

function buildPreviewItemTransforms(
  document: CurvaDocument,
  previewById: Map<string, RenderPreview>,
  ignoreChildConstraints = false,
): Map<string, PreviewItemTransform> {
  const result = new Map<string, PreviewItemTransform>();
  for (const page of document.pages) {
    for (const node of page.children) {
      collectPreviewItemTransforms(document, node, previewById, result, undefined, ignoreChildConstraints);
    }
  }
  return result;
}

function collectDynamicItemIds(
  document: CurvaDocument,
  previewById: Map<string, RenderPreview>,
  vectorPreviewNodeId: string | undefined,
): Set<string> {
  const result = new Set<string>();
  for (const page of document.pages) {
    for (const node of page.children) {
      collectDynamicItemIdsFromNode(node, previewById, vectorPreviewNodeId, false, result);
    }
  }
  return result;
}

function booleanSourcePreviewBounds(document: CurvaDocument, previewById: Map<string, RenderPreview>): Map<string, RenderPreview["bounds"]> | undefined {
  if (previewById.size === 0) {
    return undefined;
  }

  const sourceIds = new Set<string>();
  for (const node of getAllSceneNodes(document)) {
    if (node.type === "boolean") {
      for (const source of node.sourceNodes) {
        sourceIds.add(source.id);
      }
    }
  }

  const result = new Map<string, RenderPreview["bounds"]>();
  for (const [nodeId, preview] of previewById) {
    if (sourceIds.has(nodeId)) {
      result.set(nodeId, preview.bounds);
    }
  }
  return result.size > 0 ? result : undefined;
}

function collectDynamicItemIdsFromNode(
  node: SceneNode,
  previewById: Map<string, RenderPreview>,
  vectorPreviewNodeId: string | undefined,
  parentDynamic: boolean,
  result: Set<string>,
): boolean {
  const dynamic = parentDynamic || previewById.has(node.id) || node.id === vectorPreviewNodeId;
  if (dynamic) {
    result.add(node.id);
  }

  const children = getSceneNodeChildren(node);
  if (!children) {
    return dynamic;
  }

  let childDynamic = false;
  for (const child of children) {
    childDynamic = collectDynamicItemIdsFromNode(child, previewById, vectorPreviewNodeId, dynamic, result) || childDynamic;
  }
  if (node.type === "boolean" && childDynamic) {
    result.add(node.id);
    return true;
  }
  return dynamic || childDynamic;
}

function intersectBounds(left: RenderPreview["bounds"], right: RenderPreview["bounds"]): RenderPreview["bounds"] {
  const x1 = Math.max(left.x, right.x);
  const y1 = Math.max(left.y, right.y);
  const x2 = Math.min(left.x + left.width, right.x + right.width);
  const y2 = Math.min(left.y + left.height, right.y + right.height);
  return {
    x: x1,
    y: y1,
    width: Math.max(0, x2 - x1),
    height: Math.max(0, y2 - y1),
  };
}

function rectsIntersect(left: RenderPreview["bounds"], right: RenderPreview["bounds"]): boolean {
  return left.x < right.x + right.width &&
    left.x + left.width > right.x &&
    left.y < right.y + right.height &&
    left.y + left.height > right.y;
}

function collectPreviewItemTransforms(
  document: CurvaDocument,
  node: SceneNode,
  previewById: Map<string, RenderPreview>,
  result: Map<string, PreviewItemTransform>,
  parentTransform?: { from: RenderPreview["bounds"]; to: RenderPreview["bounds"] },
  ignoreChildConstraints = false,
  activeClip?: DisplayClip,
): void {
  const ownPreview = previewById.get(node.id);
  const ownPreviewBounds = ownPreview?.bounds;
  const inheritedBounds = parentTransform ? transformBounds(node.bounds, parentTransform.from, parentTransform.to) : undefined;
  const nextBounds = ownPreviewBounds ?? inheritedBounds;
  let childClip = activeClip;
  let nextWorldTransform: Affine2D | undefined;
  if (nextBounds) {
    const previews = scenePreviewsForNode(previewById, result, node.id, nextBounds, ownPreview?.rotation);
    const worldOriginDelta = previewWorldOriginDelta(document, node.id, node.bounds, nextBounds, previews);
    const worldTransform = nodeDisplayWorldTransform(document, node.id, previews);
    nextWorldTransform = worldTransform;
    result.set(node.id, {
      bounds: nextBounds,
      rotation: ownPreview?.rotation,
      rotationDelta: ownPreview?.rotation === undefined ? undefined : ownPreview.rotation - node.transform.rotation,
      transformClip: ownPreviewBounds === undefined && inheritedBounds !== undefined,
      clip: activeClip,
      worldOriginDelta,
      worldTransform,
    });
  }

  const children = getSceneNodeChildren(node);
  if (!children) {
    return;
  }

  if (node.type === "frame" && node.clipsContent && nextBounds) {
    childClip = intersectDisplayClips(activeClip, transformedRectClip(nextBounds, nextWorldTransform));
  }

  if (
    node.type === "frame" &&
    nextBounds !== undefined &&
    !boundsArePureMove(node.bounds, nextBounds) &&
    frameAutoLayoutMode(node) === "off" &&
    !ignoreChildConstraints
  ) {
    for (const child of children) {
      const childBounds = applyConstraintsToBounds(child.bounds, node.bounds, nextBounds, nodeConstraints(child));
      collectPreviewItemTransforms(document, child, previewById, result, { from: child.bounds, to: childBounds }, ignoreChildConstraints, childClip);
    }
    return;
  }

  const shouldTransformChildren =
    node.type === "group" ||
    node.type === "component" ||
    node.type === "componentVariant" ||
    node.type === "instance" ||
    node.type === "boolean" ||
    (node.type === "frame" && nextBounds !== undefined && boundsArePureMove(node.bounds, nextBounds));
  const nextTransform = nextBounds && shouldTransformChildren
    ? { from: node.bounds, to: nextBounds }
    : parentTransform;

  for (const child of children) {
    collectPreviewItemTransforms(document, child, previewById, result, nextTransform, ignoreChildConstraints, childClip);
  }
}

function scenePreviewsForNode(
  previewById: Map<string, RenderPreview>,
  result: Map<string, PreviewItemTransform>,
  nodeId: string,
  bounds: RenderPreview["bounds"],
  rotation?: number,
): ScenePreview[] {
  const merged = new Map<string, ScenePreview>();
  for (const [id, preview] of previewById) {
    merged.set(id, { nodeId: id, bounds: preview.bounds, rotation: preview.rotation });
  }
  for (const [id, transform] of result) {
    merged.set(id, { nodeId: id, bounds: transform.bounds, rotation: transform.rotation });
  }
  merged.set(nodeId, { nodeId, bounds, rotation });
  return [...merged.values()];
}

function previewWorldOriginDelta(
  document: CurvaDocument,
  nodeId: string,
  fromBounds: RenderPreview["bounds"],
  toBounds: RenderPreview["bounds"],
  previews: ScenePreview[],
): { x: number; y: number } | undefined {
  const start = nodeSelectionPointToWorld(document, nodeId, { x: fromBounds.x, y: fromBounds.y }, []);
  const end = nodeSelectionPointToWorld(document, nodeId, { x: toBounds.x, y: toBounds.y }, previews);
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) {
    return undefined;
  }
  return { x: dx, y: dy };
}

function offsetDisplayPaint(paint: DisplayPaint, dx: number, dy: number): DisplayPaint {
  if (paint.type === "linearGradient") {
    return {
      ...paint,
      start: [paint.start[0] + dx, paint.start[1] + dy],
      end: [paint.end[0] + dx, paint.end[1] + dy],
    };
  }

  if (paint.type === "radialGradient") {
    return {
      ...paint,
      center: [paint.center[0] + dx, paint.center[1] + dy],
    };
  }

  if (paint.type === "image") {
    return {
      ...paint,
      imageRect: offsetBounds(paint.imageRect, dx, dy),
    };
  }

  return paint;
}

function transformVertices(vertices: ArrayLike<number>, fromBounds: RenderPreview["bounds"], toBounds: RenderPreview["bounds"]): Float32Array {
  const scaleX = fromBounds.width === 0 ? 1 : toBounds.width / fromBounds.width;
  const scaleY = fromBounds.height === 0 ? 1 : toBounds.height / fromBounds.height;
  const transformed = new Float32Array(vertices.length);
  for (let index = 0; index < vertices.length; index += 2) {
    transformed[index] = toBounds.x + (vertices[index]! - fromBounds.x) * scaleX;
    transformed[index + 1] = toBounds.y + (vertices[index + 1]! - fromBounds.y) * scaleY;
  }
  return transformed;
}

function transformFlatVerticesByAffine(vertices: ArrayLike<number>, matrix: Affine2D): Float32Array {
  const transformed = new Float32Array(vertices.length);
  for (let index = 0; index < vertices.length; index += 2) {
    const point = applyAffinePoint(matrix, { x: vertices[index]!, y: vertices[index + 1]! });
    transformed[index] = point.x;
    transformed[index + 1] = point.y;
  }
  return transformed;
}

function transformDisplayPaintByAffine(paint: DisplayPaint, matrix: Affine2D): DisplayPaint {
  if (paint.type === "linearGradient") {
    const start = applyAffinePoint(matrix, { x: paint.start[0], y: paint.start[1] });
    const end = applyAffinePoint(matrix, { x: paint.end[0], y: paint.end[1] });
    return { ...paint, start: [start.x, start.y], end: [end.x, end.y] };
  }

  if (paint.type === "radialGradient") {
    const center = applyAffinePoint(matrix, { x: paint.center[0], y: paint.center[1] });
    const scale = Math.hypot(matrix.a, matrix.b);
    return { ...paint, center: [center.x, center.y], radius: paint.radius * scale };
  }

  if (paint.type === "image") {
    return {
      ...paint,
      imageRect: boundsFromVertices(transformFlatVerticesByAffine(rectVertices(paint.imageRect), matrix)),
    };
  }

  return paint;
}

function rotatePreviewVertices(vertices: ArrayLike<number>, center: { x: number; y: number }, rotation: number): Float32Array {
  const rotated = new Float32Array(vertices.length);
  for (let index = 0; index < vertices.length; index += 2) {
    const point = rotatePoint({ x: vertices[index]!, y: vertices[index + 1]! }, center, rotation);
    rotated[index] = point.x;
    rotated[index + 1] = point.y;
  }
  return rotated;
}

function rotatePreviewPaint(paint: DisplayPaint, center: { x: number; y: number }, rotation: number): DisplayPaint {
  if (paint.type === "linearGradient") {
    const start = rotatePoint({ x: paint.start[0], y: paint.start[1] }, center, rotation);
    const end = rotatePoint({ x: paint.end[0], y: paint.end[1] }, center, rotation);
    return { ...paint, start: [start.x, start.y], end: [end.x, end.y] };
  }

  if (paint.type === "radialGradient") {
    const radialCenter = rotatePoint({ x: paint.center[0], y: paint.center[1] }, center, rotation);
    return { ...paint, center: [radialCenter.x, radialCenter.y] };
  }

  if (paint.type === "image") {
    return paint;
  }

  return paint;
}

function boundsFromVertices(vertices: ArrayLike<number>): RenderPreview["bounds"] {
  if (vertices.length < 2) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let left = vertices[0]!;
  let top = vertices[1]!;
  let right = left;
  let bottom = top;
  for (let index = 2; index < vertices.length; index += 2) {
    const x = vertices[index]!;
    const y = vertices[index + 1]!;
    left = Math.min(left, x);
    top = Math.min(top, y);
    right = Math.max(right, x);
    bottom = Math.max(bottom, y);
  }
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function transformBounds(
  bounds: RenderPreview["bounds"],
  fromBounds: RenderPreview["bounds"],
  toBounds: RenderPreview["bounds"],
): RenderPreview["bounds"] {
  const scaleX = fromBounds.width === 0 ? 1 : toBounds.width / fromBounds.width;
  const scaleY = fromBounds.height === 0 ? 1 : toBounds.height / fromBounds.height;
  return {
    x: toBounds.x + (bounds.x - fromBounds.x) * scaleX,
    y: toBounds.y + (bounds.y - fromBounds.y) * scaleY,
    width: bounds.width * scaleX,
    height: bounds.height * scaleY,
  };
}

function boundsArePureMove(fromBounds: RenderPreview["bounds"], toBounds: RenderPreview["bounds"]): boolean {
  return fromBounds.width === toBounds.width && fromBounds.height === toBounds.height;
}

function offsetBounds(bounds: RenderPreview["bounds"], dx: number, dy: number): RenderPreview["bounds"] {
  return {
    ...bounds,
    x: bounds.x + dx,
    y: bounds.y + dy,
  };
}

function addPositionOffset(current: DisplayItem["positionOffset"], dx: number, dy: number): NonNullable<DisplayItem["positionOffset"]> | undefined {
  const x = (current?.x ?? 0) + dx;
  const y = (current?.y ?? 0) + dy;
  return x === 0 && y === 0 ? undefined : { x, y };
}

function previewVectorNetwork(document: CurvaDocument, overlay: RenderOverlay): VectorNetworkPreview | undefined {
  if (overlay.vectorTopologyPreview) {
    const topology = overlay.vectorTopologyPreview;
    const node = getAllSceneNodes(document).find((candidate): candidate is Extract<SceneNode, { type: "vectorNetwork" }> =>
      candidate.type === "vectorNetwork" && candidate.id === topology.nodeId,
    );
    if (!node) {
      return undefined;
    }
    return {
      id: node.id,
      network: topology.network,
      bounds: getVectorNetworkBounds(topology.network),
      changedVertexIds: topology.changedVertexIds,
      changedEdgeIds: topology.changedEdgeIds,
    };
  }

  const nodeId = overlay.vertexPreview?.nodeId ?? overlay.handlePreview?.nodeId ?? overlay.edgeBendPreview?.nodeId;
  if (!nodeId) {
    return undefined;
  }

  const node = getAllSceneNodes(document).find((candidate): candidate is Extract<SceneNode, { type: "vectorNetwork" }> => candidate.type === "vectorNetwork" && candidate.id === nodeId);
  if (!node) {
    return undefined;
  }

  const network: VectorNetwork = {
    fillRule: node.network.fillRule,
    fillOverrideTable: node.network.fillOverrideTable ? structuredClone(node.network.fillOverrideTable) : undefined,
    regions: node.network.regions.map(region => ({ ...region, edgeIds: [...region.edgeIds] })),
    vertices: node.network.vertices.map(vertex => ({ ...vertex })),
    edges: node.network.edges.map(edge => ({
      ...edge,
      from: { vertexId: edge.from.vertexId, handle: edge.from.handle ? { ...edge.from.handle } : undefined },
      to: { vertexId: edge.to.vertexId, handle: edge.to.handle ? { ...edge.to.handle } : undefined },
    })),
  };

  if (overlay.vertexPreview?.nodeId === node.id) {
    for (const preview of overlay.vertexPreview.vertices ?? [overlay.vertexPreview]) {
      const vertex = network.vertices.find(candidate => candidate.id === preview.vertexId);
      if (!vertex) {
        continue;
      }

      const dx = preview.x - vertex.x;
      const dy = preview.y - vertex.y;
      vertex.x = preview.x;
      vertex.y = preview.y;
      translateConnectedHandles(network, vertex.id, dx, dy);
    }
    for (const preview of overlay.vertexPreview.handles ?? []) {
      const edge = network.edges.find(candidate => candidate.id === preview.edgeId);
      if (edge) {
        edge[preview.endpoint].handle = { x: preview.x, y: preview.y };
      }
    }
  }

  if (overlay.handlePreview?.nodeId === node.id) {
    const edge = network.edges.find(candidate => candidate.id === overlay.handlePreview?.edgeId);
    if (edge) {
      edge[overlay.handlePreview.endpoint].handle = {
        x: overlay.handlePreview.x,
        y: overlay.handlePreview.y,
      };
    }
    if (overlay.handlePreview.mirroredHandle) {
      const mirroredEdge = network.edges.find(candidate => candidate.id === overlay.handlePreview?.mirroredHandle?.edgeId);
      if (mirroredEdge) {
        mirroredEdge[overlay.handlePreview.mirroredHandle.endpoint].handle = {
          x: overlay.handlePreview.mirroredHandle.x,
          y: overlay.handlePreview.mirroredHandle.y,
        };
      }
    }
  }

  if (overlay.edgeBendPreview?.nodeId === node.id) {
    const edge = network.edges.find(candidate => candidate.id === overlay.edgeBendPreview?.edgeId);
    if (edge) {
      edge.from.handle = { ...overlay.edgeBendPreview.fromHandle };
      edge.to.handle = { ...overlay.edgeBendPreview.toHandle };
    }
  }

  return {
    id: node.id,
    network,
    bounds: getVectorNetworkBounds(network),
    changedVertexIds: previewChangedVertexIds(overlay, node.id),
    changedEdgeIds: previewChangedEdgeIds(overlay, node.id),
  };
}

function previewChangedVertexIds(overlay: RenderOverlay, nodeId: string): string[] {
  if (overlay.vertexPreview?.nodeId !== nodeId) {
    return [];
  }

  const ids = new Set<string>();
  for (const preview of overlay.vertexPreview.vertices ?? [overlay.vertexPreview]) {
    ids.add(preview.vertexId);
  }
  return [...ids];
}

function previewChangedEdgeIds(overlay: RenderOverlay, nodeId: string): string[] {
  const ids = new Set<string>();
  if (overlay.vertexPreview?.nodeId === nodeId) {
    for (const handle of overlay.vertexPreview.handles ?? []) {
      ids.add(handle.edgeId);
    }
  }
  if (overlay.handlePreview?.nodeId === nodeId) {
    ids.add(overlay.handlePreview.edgeId);
    if (overlay.handlePreview.mirroredHandle) {
      ids.add(overlay.handlePreview.mirroredHandle.edgeId);
    }
  }
  if (overlay.edgeBendPreview?.nodeId === nodeId) {
    ids.add(overlay.edgeBendPreview.edgeId);
  }
  return [...ids];
}
